export enum TariffVersionStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum PricingType {
  FLAT = 'FLAT',
  PER_UNIT = 'PER_UNIT',
}

export enum SurchargeType {
  /** Forfait par envoi, indépendant de la quantité. */
  FIXED = 'FIXED',
  /** Pourcentage du prix de base ou du sous-total. */
  PERCENT = 'PERCENT',
  /** Montant multiplié par la quantité facturée (ex. 3 €/palette). */
  PER_UNIT = 'PER_UNIT',
}

export enum SurchargeBase {
  BASE = 'BASE',
  SUBTOTAL = 'SUBTOTAL',
}

export enum ImportJobStatus {
  PENDING = 'PENDING',
  PARSING = 'PARSING',
  PREVIEW_READY = 'PREVIEW_READY',
  CONFIRMING = 'CONFIRMING',
  COMMITTED = 'COMMITTED',
  FAILED = 'FAILED',
  ROLLED_BACK = 'ROLLED_BACK',
}

export enum AiImportJobStatus {
  PENDING = 'PENDING',
  EXTRACTING = 'EXTRACTING',
  AI_PROCESSING = 'AI_PROCESSING',
  VALIDATING = 'VALIDATING',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  READY_TO_CONFIRM = 'READY_TO_CONFIRM',
  CONFIRMING = 'CONFIRMING',
  COMMITTED = 'COMMITTED',
  FAILED = 'FAILED',
  // Grid import statuses
  ANALYZING = 'ANALYZING',
  MAPPING_REVIEW = 'MAPPING_REVIEW',
  PARSING = 'PARSING',
  PARSED = 'PARSED',
}

export enum OriginKeyType {
  DEPARTMENT = 'DEPARTMENT',
  POSTAL_CODE = 'POSTAL_CODE',
  ZONE = 'ZONE',
  ALL = 'ALL',
}

export enum DestKeyType {
  DEPARTMENT = 'DEPARTMENT',
  POSTAL_CODE = 'POSTAL_CODE',
  POSTAL_PREFIX = 'POSTAL_PREFIX',
  ZONE = 'ZONE',
  ALL = 'ALL',
}

// ─── Ancillary Rules ──────────────────────────────────────
export enum AncillaryTiming {
  QUOTE = 'QUOTE',
  EVENT = 'EVENT',
  INVOICE = 'INVOICE',
}

export enum AncillaryRuleStatus {
  ACTIVE = 'ACTIVE',
  INCLUDED = 'INCLUDED',
  ON_QUOTE = 'ON_QUOTE',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum AncillaryCalcType {
  FIXED = 'FIXED',
  PERCENT = 'PERCENT',
  PER_UNIT = 'PER_UNIT',
}

export enum AncillaryCalcBase {
  BASE_PRICE = 'BASE_PRICE',
  SUBTOTAL = 'SUBTOTAL',
  GOODS_VALUE = 'GOODS_VALUE',
}

export enum AncillaryPerUnit {
  SHIPMENT = 'SHIPMENT',
  KG = 'KG',
}

export enum AncillarySourceType {
  AI_IMPORT = 'AI_IMPORT',
  MANUAL = 'MANUAL',
}

export enum AncillaryApplicationStatus {
  APPLIED = 'APPLIED',
  NOT_APPLIED = 'NOT_APPLIED',
  NOT_APPLIED_MISSING_INPUT = 'NOT_APPLIED_MISSING_INPUT',
  NOT_APPLIED_INFO = 'NOT_APPLIED_INFO',
}

export enum AncillaryImportJobStatus {
  PENDING = 'PENDING',
  EXTRACTING = 'EXTRACTING',
  AI_PROCESSING = 'AI_PROCESSING',
  VALIDATING = 'VALIDATING',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  READY_TO_CONFIRM = 'READY_TO_CONFIRM',
  CONFIRMING = 'CONFIRMING',
  CONFIRMED = 'CONFIRMED',
  FAILED = 'FAILED',
}

// ─── Energy / Fuel Surcharge ──────────────────────────────
export enum FuelClauseStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum FuelClauseScopeMode {
  STANDARD = 'STANDARD',
  SPOT_NO_PASS = 'SPOT_NO_PASS',
  SUBCONTRACTED_CASCADE = 'SUBCONTRACTED_CASCADE',
}

export enum MonthlyIndexSource {
  MANUAL = 'MANUAL',
  AUTO_CNR = 'AUTO_CNR',
  /** Relevé automatique d'une référence de marché (BCE, EIA) : Brent, parité €/$. */
  AUTO_MARKET = 'AUTO_MARKET',
  AI_IMPORT = 'AI_IMPORT',
  SEED = 'SEED',
}

/** Périodicité de publication d'une série d'indices. */
export enum IndexFrequency {
  /** Une valeur par mois, période = mois civil. */
  MONTHLY = 'MONTHLY',
  /** Deux valeurs par mois : 1ère quinzaine (1-15) et 2ème quinzaine (16-fin). */
  BIMONTHLY = 'BIMONTHLY',
}

/**
 * Nature de la donnée portée par une série. C'est ce qui sépare une énergie
 * consommée d'une simple référence de marché, et un prix d'un indice : les
 * trois ne se comparent pas et ne s'additionnent jamais dans une clause.
 *
 * Les valeurs sont celles du contrat de l'API publique (`/public/indices`),
 * d'où la casse : ne pas les renommer sans prévenir les consommateurs.
 */
export enum IndexKind {
  /** Prix unitaire exprimé dans `unit` (€/L, €/kg…). */
  PRICE = 'price',
  /** Indice sans dimension, ramené à 100 à sa date de base (`baseLabel`). */
  INDEX_BASE100 = 'index_base100',
  /** Référence de marché (Brent, PEG, parité) : contrôle, pas une énergie. */
  MARKET_REFERENCE = 'market_reference',
}

/** Origine du déclenchement d'une collecte d'indices. */
export enum IndexCollectionTrigger {
  CRON = 'CRON',
  MANUAL = 'MANUAL',
  UPLOAD = 'UPLOAD',
}

/** Issue d'une collecte d'indices. */
export enum IndexCollectionStatus {
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  /** Au moins une source collectée, au moins une en échec. */
  PARTIAL = 'PARTIAL',
  FAILED = 'FAILED',
}

// ─── Opérations : prise d'ordres & suivi ──────────────────
// Machine à états de l'Ordre — l'envoi affrétable, grain unique des
// opérations. Un ordre = une expédition, telle que la livre le système
// amont (WMS/TMS/ERP) : le regroupement des commandes est fait là-bas.
export enum OrdreStatut {
  BROUILLON = 'BROUILLON',
  PRET_A_AFFRETER = 'PRET_A_AFFRETER',
  DEMANDE_ENVOYEE = 'DEMANDE_ENVOYEE',
  CONFIRME = 'CONFIRME',
  REFUSE = 'REFUSE',
  EN_EXECUTION = 'EN_EXECUTION',
  LIVRE = 'LIVRE',
  CLOTURE = 'CLOTURE',
  ANNULE = 'ANNULE',
}

// Type de jalon de suivi porté par un ÉvénementStatut, cf. spec §4.4.
export enum JalonType {
  ACCEPTE = 'ACCEPTE',
  ENLEVE = 'ENLEVE',
  EN_TRANSIT = 'EN_TRANSIT',
  LIVRE = 'LIVRE',
  POD = 'POD',
  EXCEPTION = 'EXCEPTION',
}

// Canal par lequel une capture de statut entrante est arrivée.
export enum CanalSource {
  LIEN = 'LIEN',
  WHATSAPP = 'WHATSAPP',
  EMAIL_PARSE = 'EMAIL_PARSE',
  MANUEL = 'MANUEL',
  API = 'API',
}

// Canal de communication sortant vers le transporteur, cf. spec §9.
export enum CanalCommunication {
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  LIEN = 'LIEN',
}

// État de la réponse du transporteur à une demande d'affrètement.
export enum ConfirmationEtat {
  CONFIRME = 'CONFIRME',
  REFUSE = 'REFUSE',
}

// ─── Cotation spot (mini-AO transporteur, portail tokenisé) ───
export enum CotationRequestStatut {
  OUVERTE = 'OUVERTE',
  CLOTUREE = 'CLOTUREE',
  ATTRIBUEE = 'ATTRIBUEE',
}

export enum CotationInvitationStatut {
  ENVOYEE = 'ENVOYEE',
  VUE = 'VUE',
  COTEE = 'COTEE',
  DECLINEE = 'DECLINEE',
}

// ─── Locations / carnet de partenaires (Opérations) ───
/**
 * Rôle d'une location dans le carnet de partenaires. Une location peut cumuler
 * plusieurs rôles (un site peut être à la fois client et hub, p. ex.).
 */
export enum LocationRole {
  FOURNISSEUR = 'FOURNISSEUR',
  CLIENT = 'CLIENT',
  HUB = 'HUB',
}

// ─── Import de grilles tarifaires multi-transporteurs (Data) ───

/**
 * Cas d'usage d'import. Il pilote tout : les champs proposés au mapping, les
 * étapes du parcours, et ce que le commit écrit en base. Ajouter un cas de
 * figure = ajouter un profil, pas une page.
 */
export enum PricingGridImportProfile {
  /**
   * Tarifs camion complet : une ligne = 1 transporteur × 1 départ × 1 arrivée
   * × 1 prix, sur une tranche unique. Deux lignes identiques sont un doublon.
   */
  FULL_TRUCK = 'FULL_TRUCK',
  /**
   * Tarifs par tranche (palettes, poids, mètres plancher) : plusieurs lignes
   * par lane, chacune portant une tranche. Elles se cumulent au lieu de se
   * concurrencer.
   */
  BANDED = 'BANDED',
  /**
   * Coûts annexes seuls : ni lane, ni zone. Une ligne = 1 transporteur et ses
   * montants annexes.
   */
  ANCILLARY = 'ANCILLARY',
}

export enum PricingGridImportStatus {
  /** Créé, fichier pas encore déposé. */
  DRAFT = 'DRAFT',
  /** Fichier chargé : onglet et colonnes restent à confirmer. */
  MAPPING = 'MAPPING',
  /** Lignes extraites selon le mapping, rapprochements pas encore confirmés. */
  PARSED = 'PARSED',
  /** Décisions enregistrées, prêt à écrire en base. */
  MATCHED = 'MATCHED',
  COMMITTED = 'COMMITTED',
  FAILED = 'FAILED',
}
